/**
 * The layout switcher, and the picker that adds panels to one.
 *
 * ## Named layouts, not one arrangement
 *
 * A trader has more than one screen in their head: what they want during the
 * open is not what they want reviewing at five o'clock. Named layouts they
 * switch between is the shape that fits — and because they are stored
 * server-side, the arrangement made on a desktop is there on a laptop without
 * being made twice.
 *
 * ## Presets are copied, never applied
 *
 * "New from a preset" takes a copy at that moment. AEGIS never re-applies a
 * preset over a layout somebody has edited: a layout that silently reverts is
 * the behaviour that makes people stop trusting one to stay put.
 *
 * ## The panel list comes from the server
 *
 * `panels` arrives with the workspaces rather than being hard-coded here,
 * because the set of panels is a property of the build. A client carrying its
 * own copy would offer one this server does not have, and the picker would add
 * a panel that renders as a hole.
 */
import { useState } from 'react';
import type { JSX } from 'react';
import type { PanelSpecDto, WorkspaceDto } from '@aegis/api-client';
import type { PanelKind, Workspace } from '@aegis/core';
import {
  WORKSPACE_PRESETS, addPanel, attachPane, fromPreset, hasPanel, paneKind,
} from '@aegis/core';
import { Icon } from './Icon.js';

export function WorkspaceBar({
  all, active, activeId, panels, limit, refusal,
  onSwitch, onCreate, onRemove, onRename, onEdit, onAttach, dismissRefusal,
}: {
  all: readonly WorkspaceDto[];
  active: Workspace | null;
  activeId: string | null;
  panels: readonly PanelSpecDto[];
  limit: number;
  refusal: string | null;
  onSwitch: (id: string) => void;
  onCreate: (name: string, from?: Workspace) => void;
  onRemove: (id: string) => void;
  onRename: (name: string) => void;
  onEdit: (fn: (ws: Workspace) => Workspace) => void;
  /** Bring a detached instance home: close its window, then put it back. */
  onAttach: (paneId: string) => void;
  dismissRefusal: () => void;
}): JSX.Element | null {
  const [adding, setAdding] = useState(false);
  const [picking, setPicking] = useState(false);
  const [renaming, setRenaming] = useState(false);

  if (!active) return null;

  /*
    What the picker offers.

    A repeatable kind stays on the list however many the layout already holds —
    two charts on two symbols is the point of a workspace, and so is a ladder
    beside the instrument it is pointed at. A single-instance kind drops off
    once it is placed, because adding a second Rule tracker would only move the
    one that exists.
  */
  const addable = panels.filter((p) => p.multiple || !hasPanel(active, p.kind as PanelKind));

  return (
    <div className="ws-bar">
      <div className="ws-tabs" role="tablist" aria-label="Layouts">
        {all.map((w) => (
          <button
            key={w.id}
            type="button"
            role="tab"
            aria-selected={w.id === activeId}
            className={`chip-btn compact ${w.id === activeId ? 'on' : ''}`}
            onClick={() => onSwitch(w.id)}
          >
            {w.name}
          </button>
        ))}

        <button
          type="button"
          className="chip-btn compact"
          onClick={() => setAdding((v) => !v)}
          aria-expanded={adding}
          disabled={all.length >= limit}
          title={all.length >= limit ? `You can keep ${limit} layouts.` : 'New layout'}
        >
          <Icon name="plus" size="sm" /> Layout
        </button>
      </div>

      <div className="ws-bar-actions">
        {renaming ? (
          <input
            className="ws-rename"
            autoFocus
            defaultValue={active.name}
            maxLength={40}
            aria-label="Layout name"
            onBlur={(e) => { onRename(e.target.value); setRenaming(false); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') setRenaming(false);
            }}
          />
        ) : (
          <button type="button" className="chip-btn compact" onClick={() => setRenaming(true)}>
            Rename
          </button>
        )}

        <button
          type="button"
          className="chip-btn compact"
          onClick={() => setPicking((v) => !v)}
          aria-expanded={picking}
          disabled={addable.length === 0}
          title={addable.length === 0 ? 'Every panel is already in this layout.' : undefined}
        >
          <Icon name="plus" size="sm" /> Panel
        </button>

        <button
          type="button"
          className="chip-btn compact"
          onClick={() => onRemove(active.id)}
          disabled={all.length <= 1}
          title={all.length <= 1 ? 'This is your only layout.' : undefined}
        >
          Delete
        </button>
      </div>

      {refusal && (
        <div className="ws-refusal" role="status">
          <span>{refusal}</span>
          <button type="button" className="chip-btn compact" onClick={dismissRefusal}>
            Dismiss
          </button>
        </div>
      )}

      {/* A detached panel is in neither column nor picker, so without this row
          the only way back is the window's own close button — and on a second
          monitor that is a window the trader may not be able to see. */}
      {active.detached.length > 0 && (
        <div className="ws-detached">
          <span className="dim">In their own windows:</span>
          {active.detached.map((paneId) => {
            const kind = paneKind(paneId);
            return (
              <button
                key={paneId}
                type="button"
                className="chip-btn compact"
                onClick={() => onAttach(paneId)}
              >
                {panels.find((p) => p.kind === kind)?.label ?? paneId} — bring back
              </button>
            );
          })}
        </div>
      )}

      {adding && (
        <div className="ws-sheet" role="menu" aria-label="New layout">
          {WORKSPACE_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              role="menuitem"
              className="ws-sheet-row"
              onClick={() => {
                // A copy taken now, never a live link to the preset.
                onCreate(preset.name, fromPreset(preset, `ws_${Date.now().toString(36)}`));
                setAdding(false);
              }}
            >
              <strong>{preset.name}</strong>
              <span className="dim">{preset.summary}</span>
            </button>
          ))}
          <button
            type="button"
            role="menuitem"
            className="ws-sheet-row"
            onClick={() => { onCreate('Untitled'); setAdding(false); }}
          >
            <strong>Empty</strong>
            <span className="dim">Start with nothing and add panels.</span>
          </button>
        </div>
      )}

      {picking && (
        <div className="ws-sheet" role="menu" aria-label="Add a panel">
          {addable.map((spec) => (
            <button
              key={spec.kind}
              type="button"
              role="menuitem"
              className="ws-sheet-row"
              onClick={() => {
                onEdit((ws) => addPanel(ws, spec.kind as PanelKind));
                setPicking(false);
              }}
            >
              <strong>{spec.label}</strong>
              <span className="dim">{spec.summary}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Re-exported so `App` composes the attach without importing the model itself. */
export function attachToWorkspace(ws: Workspace, kind: PanelKind): Workspace {
  return attachPane(ws, kind);
}
